import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import ClientQuickProfile from '../../components/common/ClientQuickProfile';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { 
  Users, Search, User, Home, Phone, AlertTriangle, 
  Pill, RefreshCw, Eye, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const ClientProfilesPage = () => {
  const { api } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/shift-notes/team/participants');
      setParticipants(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Client profiles viewing is currently disabled');
      } else {
        toast.error('Failed to load participants');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const filteredParticipants = participants.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.house_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openProfile = (participantId) => {
    setSelectedParticipant(participantId);
    setProfileDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Client Profiles"
        description="View essential care information for your assigned participants"
        icon={<Users className="h-6 w-6" />}
      />

      {/* Search and Refresh */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search participants or houses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={fetchParticipants} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Essential Care Information Only</p>
            <p className="text-blue-600 dark:text-blue-400">
              You can view basic care information (medications, allergies, emergency contacts) 
              for participants you are assigned to support. This data is read-only and all access is logged.
            </p>
          </div>
        </div>
      </div>

      {/* Participants Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filteredParticipants.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="font-semibold mb-2">No Participants Found</h3>
            <p className="text-muted-foreground text-sm">
              {searchTerm 
                ? "No participants match your search" 
                : "You don't have any assigned participants to view"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredParticipants.map(participant => (
            <Card 
              key={participant.id} 
              className="hover:border-primary/50 transition-colors cursor-pointer group"
              onClick={() => openProfile(participant.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{participant.name}</h3>
                      {participant.house_name && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Home className="h-3 w-3" />
                          {participant.house_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
                
                {/* Quick info badges */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline" className="text-xs">
                    <Eye className="h-3 w-3 mr-1" />
                    View Profile
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Participant count */}
      {!loading && filteredParticipants.length > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {filteredParticipants.length} of {participants.length} participant{participants.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Quick Profile
            </DialogTitle>
          </DialogHeader>
          {selectedParticipant && (
            <ClientQuickProfile 
              participantId={selectedParticipant}
              variant="expanded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProfilesPage;
